import React from 'react';
import { View, Text, Linking } from 'react-native';
import theme from '../../../theme';

const CONTACT_EMAIL = 'info@stackdigital.eu';

function AgentsSectionHeading({ children }) {
  return (
    <Text style={{
      fontFamily: theme.typography.fontDisplay,
      fontWeight: theme.typography.weight.regular,
      fontSize: theme.typography.size.xl,
      letterSpacing: theme.trackingPx(theme.typography.size.xl, 'tight'),
      marginBottom: 14,
    }}>
      {children}
    </Text>
  );
}

function AgentsBody({ children, style }) {
  return (
    <Text style={[{
      fontFamily: theme.typography.fontSans,
      fontSize: theme.typography.size.sm,
      color: theme.colors.textSecondary,
      lineHeight: theme.lineHeightPx(theme.typography.size.sm, 'normal'),
      marginBottom: 12,
    }, style]}>
      {children}
    </Text>
  );
}

function AgentsBullet({ children }) {
  return (
    <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
      <Text style={{ fontFamily: theme.typography.fontSans, fontSize: theme.typography.size.sm, color: theme.colors.textSecondary }}>—</Text>
      <Text style={{ flex: 1, fontFamily: theme.typography.fontSans, fontSize: theme.typography.size.sm, color: theme.colors.textSecondary, lineHeight: theme.lineHeightPx(theme.typography.size.sm, 'normal') }}>
        {children}
      </Text>
    </View>
  );
}

function AgentsSection({ children, first }) {
  return (
    <View style={{
      paddingVertical: 36,
      borderTopWidth: first ? 0 : theme.borderWidth.thin,
      borderTopColor: theme.colors.borderSubtle,
    }}>
      {children}
    </View>
  );
}

function AgentsRightCard({ title, children }) {
  return (
    <View style={{
      borderWidth: theme.borderWidth.thin,
      borderColor: theme.colors.borderDefault,
      borderRadius: theme.radii.md,
      padding: 16,
      marginBottom: 12,
    }}>
      <Text style={{ fontFamily: theme.typography.fontSans, fontWeight: theme.typography.weight.semibold, fontSize: theme.typography.size.sm, marginBottom: 4 }}>
        {title}
      </Text>
      <Text style={{ fontFamily: theme.typography.fontSans, fontSize: theme.typography.size.sm, color: theme.colors.textSecondary, lineHeight: theme.lineHeightPx(theme.typography.size.sm, 'normal') }}>
        {children}
      </Text>
    </View>
  );
}

function AgentsLink({ children }) {
  return (
    <Text
      onPress={() => Linking.openURL(`mailto:${CONTACT_EMAIL}`)}
      style={{ fontFamily: theme.typography.fontSans, fontSize: theme.typography.size.sm, color: theme.colors.accentPrimary }}
    >
      {children}
    </Text>
  );
}

export default function AgentsPrivacyArticle() {
  return (
    <View style={{ paddingVertical: 56, paddingHorizontal: 48, maxWidth: 720, width: '100%', alignSelf: 'center' }}>

      <View style={{ marginBottom: 40 }}>
        <Text style={{
          fontFamily: theme.typography.fontDisplay,
          fontWeight: theme.typography.weight.regular,
          fontSize: theme.typography.size['3xl'],
          letterSpacing: theme.trackingPx(theme.typography.size['3xl'], 'tight'),
          marginBottom: 12,
        }}>
          Privacy Policy
        </Text>
        <Text style={{
          fontFamily: theme.typography.fontMono,
          fontSize: theme.typography.size['2xs'],
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: theme.trackingPx(theme.typography.size['2xs'], 'widest'),
        }}>
          Last updated: July 2026
        </Text>
      </View>

      {/* Principles */}
      <View style={{
        backgroundColor: theme.colors.surfaceSunken,
        borderWidth: theme.borderWidth.thin,
        borderColor: theme.colors.borderDefault,
        borderRadius: theme.radii.lg,
        padding: 24,
        marginBottom: 40,
      }}>
        <Text style={{
          fontFamily: theme.typography.fontMono,
          fontSize: theme.typography.size['2xs'],
          letterSpacing: theme.trackingPx(theme.typography.size['2xs'], 'widest'),
          textTransform: 'uppercase',
          color: theme.colors.textTertiary,
          marginBottom: 12,
        }}>
          Our privacy principles
        </Text>
        <AgentsBullet>We will never spam you with irrelevant emails.</AgentsBullet>
        <AgentsBullet>You may opt in or out of our email communications at any time.</AgentsBullet>
        <AgentsBullet>You have the right to remove any information we hold on you.</AgentsBullet>
        <AgentsBullet>We will never sell, rent, make public, or distribute your personal information.</AgentsBullet>
        <AgentsBullet>Data is a liability — we only collect and process what's absolutely necessary.</AgentsBullet>
      </View>

      <AgentsSection first>
        <AgentsSectionHeading>1. Relevant privacy legislation</AgentsSectionHeading>
        <AgentsBody>In line with Stack's internal processes and systems, this website and product are designed to comply with:</AgentsBody>
        <AgentsBullet>Data Protection Act (Cap. 440 of the Laws of Malta)</AgentsBullet>
        <AgentsBullet>EU Data Protection Directive 1995 (DPD)</AgentsBullet>
        <AgentsBullet>EU General Data Protection Regulation 2018 (GDPR)</AgentsBullet>
        <AgentsBody style={{ marginTop: 6 }}>Compliance with the above is intended to align this product with data protection and privacy legislation more broadly.</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>2. Who we are</AgentsSectionHeading>
        <AgentsBody>Stack Technology Limited</AgentsBody>
        <AgentsBody>Registered office: Villa Malitah, Mediterranean Street, St. Julians, STJ1870, Malta</AgentsBody>
        <AgentsBody>Company registration number: C108757</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>3. What information do we collect about you?</AgentsSectionHeading>
        <AgentsBody><Text style={{ fontWeight: theme.typography.weight.semibold }}>Contact &amp; demo request forms.</Text> When you submit a form on our website, we collect:</AgentsBody>
        <AgentsBullet>Full name</AgentsBullet>
        <AgentsBullet>Email address</AgentsBullet>
        <AgentsBullet>Job title and company</AgentsBullet>
        <AgentsBullet>Any other information you supply relevant to your enquiry</AgentsBullet>
        <AgentsBody style={{ marginTop: 6 }}>
          <Text style={{ fontWeight: theme.typography.weight.semibold }}>Product usage.</Text> When you use the Stack agent, we store your questions and the agent's answers as your conversation history — this lets you and your team return to earlier analysis, even though the agent itself only uses your most recent questions as context for each reply.
        </AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>4. Website &amp; product usage tracking</AgentsSectionHeading>
        <AgentsBody>Like most websites, we use Google Analytics to collect anonymised information — number of visitors, where they came from, and the pages they visited. We use this to understand how people find and use Stack, and to improve the experience.</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>5. How we use your information</AgentsSectionHeading>
        <AgentsBody>We use your information to understand your needs and provide a better service. Specifically:</AgentsBody>
        <AgentsBullet>To maintain and improve our website, product, and services</AgentsBullet>
        <AgentsBullet>To collect statistical usage data</AgentsBullet>
        <AgentsBullet>To follow up on your enquiry — a member of our team may contact you directly</AgentsBullet>
        <AgentsBullet>To remarket our services to you</AgentsBullet>
        <AgentsBody style={{ marginTop: 6 }}>Stack will not share your information with third parties. We will not sell, distribute, or lease your personal information unless we have your permission or are required to by law.</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>6. Cookies</AgentsSectionHeading>
        <AgentsBody>A cookie is a piece of code that lets a web server identify and track browser activity. They help our website run efficiently and give us insight into how it's used.</AgentsBody>
        <AgentsBody>By accepting cookies on our website, you consent to any personal and sensitive data collected being processed for behavioural analytics and marketing purposes, in accordance with the Data Protection Act (Cap. 440 of the Laws of Malta).</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>7. Your rights, access &amp; correction</AgentsSectionHeading>
        <AgentsBody>We will always let you access the personal and sensitive data we hold about you, on written request. In detail, you have the right to:</AgentsBody>

        <AgentsRightCard title="Right of access">Request a copy of all personal and sensitive data we hold and process about you.</AgentsRightCard>
        <AgentsRightCard title="Right to withdraw consent">Withdraw consent and opt out at any time — we'll stop processing your data once you do.</AgentsRightCard>
        <AgentsRightCard title="Right to rectification">Request corrections to any personal and sensitive data we hold about you.</AgentsRightCard>
        <AgentsRightCard title={'Right to erasure ("right to be forgotten")'}>Request that we delete all personal and sensitive data we hold about you.</AgentsRightCard>
        <AgentsRightCard title="Right to restriction of processing">Request that we pause processing for a particular activity.</AgentsRightCard>
        <AgentsRightCard title="Right to data portability">Request a copy of your data in a portable format.</AgentsRightCard>
        <AgentsRightCard title="Right to object">Request that we stop all future processing and remove all data we hold about you.</AgentsRightCard>

        <AgentsBody style={{ marginTop: 16 }}>
          <Text style={{ fontWeight: theme.typography.weight.semibold }}>Data retention.</Text> We retain your data for a maximum of one (1) year. If you don't opt in, all data is deleted within one (1) month.
        </AgentsBody>
        <AgentsBody>
          All data protection enquiries or complaints should be sent to <AgentsLink>{CONTACT_EMAIL}</AgentsLink>. You also have the right to lodge a complaint with the Information and Data Protection Commissioner in Malta, the relevant supervisory authority.
        </AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>8. Security</AgentsSectionHeading>
        <AgentsBody>We're committed to keeping your information secure. To prevent unauthorised access or disclosure, we maintain suitable physical, electronic, and managerial safeguards around everything we collect online.</AgentsBody>
      </AgentsSection>

      <AgentsSection>
        <AgentsSectionHeading>9. How to contact us</AgentsSectionHeading>
        <AgentsBody>
          If you have any questions about this policy, or the information we hold about you, please get in touch at <AgentsLink>{CONTACT_EMAIL}</AgentsLink>.
        </AgentsBody>
      </AgentsSection>

    </View>
  );
}